'use client';
import NiceModal from '@ebay/nice-modal-react';
import { sharePage } from '@repo/common-utils';
import { useWorkingPlatform } from '@repo/platform';
import { useUserEventLogger } from '@repo/tracking-service';
import { IconButton, TextButton, useDialog, useToast } from '@repo/ui';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { JoinsInformationUseModal, SenaInformationUseModal, SenaIntroduceModal } from '@/app/_components/SenaIntroduceModal';
import { COMMON_UI_HIDDEN_PARTNERS } from '@/lib/constants/platform';
import { useChatMessage } from '@/lib/hooks/use-chat-message';
import { useExitService } from '@/lib/hooks/use-navigation';
import { LayoutHeaderProps } from '@/lib/types/layout';

export const HeaderRightButtons = ({ isUseInformationUse, isUseIntroduce, isUseAppMenu, isUseShare, isUseNewCHat }: LayoutHeaderProps) => {
  const router = useRouter();
  const { toast } = useToast();
  const { sendClickEvent } = useUserEventLogger();
  const { generateNewChatId } = useChatMessage();
  const { workingPlatform } = useWorkingPlatform();
  const isUiHiddenPartners = COMMON_UI_HIDDEN_PARTNERS.includes(workingPlatform);

  return (
    <div className={'flex items-center gap-tiny-small'}>
      {isUseShare && !isUiHiddenPartners && (
        <TextButton
          variant={'secondary'}
          size={'small'}
          type={'button'}
          onClick={() => {
            sendClickEvent('topnav_share');
            sharePage(
              {
                title: 'AI 세무·법률·노무 무료 상담 서비스',
                url: window.location.href
              },
              () => {
                toast({
                  title: '링크를 복사했어요.',
                  variant: 'success'
                });
              }
            );
          }}
        >
          공유하기
        </TextButton>
      )}
      {isUseIntroduce && !isUiHiddenPartners && (
        <TextButton
          variant={'secondary'}
          size={'small'}
          type={'button'}
          onClick={() => {
            sendClickEvent('topnav_introduce-sena');
            NiceModal.show(SenaIntroduceModal);
          }}
        >
          세나 소개
        </TextButton>
      )}
      {isUseInformationUse && isUiHiddenPartners && (
        <TextButton
          variant={'secondary'}
          size={'small'}
          type={'button'}
          onClick={() => {
            sendClickEvent('topnav_information-use');
            if (workingPlatform === 'joins-hr') {
              NiceModal.show(JoinsInformationUseModal);
              return;
            }
            NiceModal.show(SenaInformationUseModal);
          }}
        >
          이용 안내
        </TextButton>
      )}
      {isUseNewCHat && (
        <TextButton
          variant={'secondary'}
          size={'small'}
          type={'button'}
          onClick={() => {
            sendClickEvent('topnav_new-chat');
            generateNewChatId();
            router.push('/chat');
          }}
        >
          새 채팅
        </TextButton>
      )}
      {isUseAppMenu && !isUiHiddenPartners && (
        <IconButton
          variant={'main'}
          size={'small'}
          iconType={'menu'}
          onClick={() => {
            sendClickEvent('topnav_more');
            router.push('/app-menu');
          }}
          aria-label={'app-menu'}
        />
      )}
    </div>
  );
};

export const LogoHeaderLeftButtons = () => {
  const { isPartnerPlatform, browserType } = useWorkingPlatform();

  return <>{browserType === 'webview' && isPartnerPlatform ? <HeaderCloseButton /> : <HeaderSenaLogoButton />}</>;
};

export const HeaderSenaLogoButton = () => {
  const { sendClickEvent } = useUserEventLogger();
  const { workingPlatform } = useWorkingPlatform();

  return (
    <Link href={'/'} scroll={true} className={'flex items-center py-tiny-medium px-normal-small w-fit h-full'}>
      {workingPlatform === 'joins-hr' ? (
        <img
          src={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}/sena/logos/logo-joins.png`}
          className={'flex items-center cursor-pointer w-auto h-[20px]'}
          alt={'Joins logo'}
          onClick={() => sendClickEvent('topnav_logo')}
        />
      ) : (
        <img
          src={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}/sena/logos/logo-bznav-sena.svg`}
          className={'flex items-center cursor-pointer w-[115px] h-[24px]'}
          alt={'sena logo'}
          onClick={() => sendClickEvent('topnav_logo')}
        />
      )}
    </Link>
  );
};

export const HeaderCloseButton = () => {
  const { showDialog } = useDialog();
  const { exitService } = useExitService();
  const { sendClickEvent } = useUserEventLogger();

  const closeWebview = () => {
    sendClickEvent('topnav_close');
    showDialog({
      title: 'AI 세무상담을 나가시겠어요?',
      buttons: {
        primary: {
          text: '나가기',
          onClick: exitService
        },
        secondary: {
          text: '취소'
        }
      }
    });
  };

  return <IconButton variant={'main'} size={'small'} iconType={'close'} onClick={closeWebview} aria-label={'go-back'} />;
};
